import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
	SITE_NAME,
	DEFAULT_DESCRIPTION,
	DEFAULT_OG_IMAGE,
	TWITTER_HANDLE,
	absoluteUrl,
	canonicalFor,
} from './siteConfig';
import { JsonLd } from './jsonLd';

export interface SeoProps {
	/** Page title without the site-name suffix — the suffix is appended here. */
	title?: string;
	description?: string;
	/** Override the canonical path. Defaults to the current route's pathname. */
	canonicalPath?: string;
	/** Social share image (relative or absolute). */
	image?: string;
	/** Open Graph type — 'website' for pages, 'article' for blog posts. */
	type?: 'website' | 'article';
	/** Emit robots noindex. Use for cart/checkout/account/token-gated pages. */
	noindex?: boolean;
	/** One or more schema.org objects to serialise as JSON-LD. */
	jsonLd?: JsonLd | JsonLd[];
	/** Suppress the " | StoriesVillage" suffix (used by the home page). */
	bareTitle?: boolean;
	/**
	 * ISO 639-1 language of the page content (e.g. 'el'). Sets <html lang> and
	 * suppresses the en/x-default hreflang pair, which would be wrong for a
	 * non-English page. Default: 'en'.
	 */
	lang?: string;
}

/**
 * Single source of truth for per-page document head content.
 *
 * Renders title, description, canonical, Open Graph, Twitter card and JSON-LD
 * via react-helmet-async. The build-time prerenderer (scripts/prerender.js)
 * snapshots the DOM *after* these tags are applied, so for prerendered routes
 * they end up in the served HTML — which is what non-JS crawlers (Facebook,
 * LinkedIn, WhatsApp, GPTBot) actually read.
 *
 * Note: hreflang is intentionally limited to `en` / `x-default`. The site has
 * six UI locales but no locale prefix in its URLs, so there is no distinct URL
 * per language to point hreflang at. Revisit if/when /de/, /fr/ … routes exist.
 */
const Seo: React.FC<SeoProps> = ({
	title,
	description,
	canonicalPath,
	image,
	type = 'website',
	noindex = false,
	jsonLd,
	bareTitle = false,
	lang = 'en',
}) => {
	const location = useLocation();

	const fullTitle = title
		? bareTitle
			? title
			: `${title} | ${SITE_NAME}`
		: SITE_NAME;
	const desc = description || DEFAULT_DESCRIPTION;
	const canonical = canonicalFor(canonicalPath ?? location.pathname);
	const ogImage = image ? absoluteUrl(image) : DEFAULT_OG_IMAGE;
	const dir = lang === 'ar' ? 'rtl' : 'ltr';

	const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

	return (
		<Helmet prioritizeSeoTags htmlAttributes={{ lang, dir }}>
			<title>{fullTitle}</title>
			<meta name='description' content={desc} />
			<link rel='canonical' href={canonical} />

			{noindex ? (
				<meta name='robots' content='noindex, nofollow' />
			) : (
				<meta name='robots' content='index, follow, max-image-preview:large' />
			)}

			{/* Non-English pages get no hreflang: there is no en counterpart URL
			    to cluster with, and a lone wrong-language pair is worse than none. */}
			{!noindex && lang === 'en' && <link rel='alternate' hrefLang='en' href={canonical} />}
			{!noindex && lang === 'en' && (
				<link rel='alternate' hrefLang='x-default' href={canonical} />
			)}

			{/* Open Graph — read by Facebook, LinkedIn, WhatsApp, Slack, iMessage */}
			<meta property='og:site_name' content={SITE_NAME} />
			<meta property='og:type' content={type} />
			<meta property='og:title' content={fullTitle} />
			<meta property='og:description' content={desc} />
			<meta property='og:url' content={canonical} />
			<meta property='og:image' content={ogImage} />
			<meta property='og:image:alt' content={fullTitle} />

			{/* Twitter/X card */}
			<meta name='twitter:card' content='summary_large_image' />
			<meta name='twitter:site' content={TWITTER_HANDLE} />
			<meta name='twitter:title' content={fullTitle} />
			<meta name='twitter:description' content={desc} />
			<meta name='twitter:image' content={ogImage} />

			{blocks.map((block, i) => (
				// eslint-disable-next-line react/no-array-index-key
				<script key={i} type='application/ld+json'>
					{JSON.stringify(block)}
				</script>
			))}
		</Helmet>
	);
};

export default Seo;
